<div class="customer-info-content">
  <h3 class="section-title">{{ sectionTitle }}</h3>
  <div class="white-pane">
    <div class="scrollable-content" (scroll)="onBookingContentScroll()">

      <!-- Customer Details -->
      <div class="form-section">
        <h4 class="section-subtitle">Customer Details</h4>
        <form class="ci-grid" (ngSubmit)="$event.preventDefault()">

          <!-- First Name with search dropdown: focusin closes any other field's search panel (blur on custom inputs is not always reliable on the host). -->
          <div class="customer-name-field" #customerSearchContainer (focusin)="onOtherFieldFocus()">
            <app-custom-input
              label="First Name"
              placeholder="Emma"
              type="text"
              [required]="false"
              [validationRules]="firstNameValidation"
              [showValidation]="true"
              [markTouchedOnInput]="true"
              [(value)]="customerForm.firstName"
              (valueChange)="onFirstNameInput($event)"
              (blur)="onSearchFieldBlur('firstName')">
            </app-custom-input>
            <div
              class="customer-search-dropdown"
              *ngIf="shouldShowDropdown('firstName')"
              (mousedown)="$event.preventDefault()">
              <ng-container *ngTemplateOutlet="searchDropdown"></ng-container>
            </div>
          </div>

          <!-- Last Name -->
          <div (click)="onOtherFieldFocus()" (focusin)="onOtherFieldFocus()">
            <app-custom-input
              label="Last Name"
              placeholder="Jackson"
              type="text"
              [required]="false"
              [validationRules]="lastNameValidation"
              [showValidation]="true"
              [markTouchedOnInput]="true"
              [(value)]="customerForm.lastName">
            </app-custom-input>
          </div>

          <!-- Phone with search dropdown -->
          <div class="customer-phone-field" #customerSearchContainer (focusin)="onOtherFieldFocus()">
            <app-custom-input
              label="Phone Number"
              placeholder="(555) 123-4567"
              type="tel"
              [required]="false"
              [validationRules]="phoneValidation"
              [showValidation]="true"
              [markTouchedOnInput]="true"
              [(value)]="customerForm.phone"
              (valueChange)="onPhoneInput($event)"
              (blur)="onSearchFieldBlur('phone')">
            </app-custom-input>
            <div
              class="customer-search-dropdown"
              *ngIf="shouldShowDropdown('phone')"
              (mousedown)="$event.preventDefault()">
              <ng-container *ngTemplateOutlet="searchDropdown"></ng-container>
            </div>
          </div>

          <!-- Email with search dropdown -->
          <div class="customer-email-field" #customerSearchContainer (focusin)="onOtherFieldFocus()">
            <app-custom-input
              label="Email"
              placeholder="emma.j@gmail.com"
              type="email"
              [required]="false"
              [validationRules]="emailValidation"
              [showValidation]="true"
              [markTouchedOnInput]="true"
              [(value)]="customerForm.email"
              (valueChange)="onEmailInput($event)"
              (blur)="onSearchFieldBlur('email')">
            </app-custom-input>
            <div
              class="customer-search-dropdown"
              *ngIf="shouldShowDropdown('email')"
              (mousedown)="$event.preventDefault()">
              <ng-container *ngTemplateOutlet="searchDropdown"></ng-container>
            </div>
          </div>

        </form>
      </div>

      <!-- Visit Information (only when existing customer is selected) -->
      <div class="form-section" *ngIf="selectedExistingCustomer">
        <h4 class="section-subtitle">Visit Information</h4>
        <div class="visit-info-container">
          <div class="visit-info-item">
            <span class="visit-info-label">Total Visits:</span>
            <span class="visit-info-value">{{ selectedExistingCustomer.visit_count || '0' }}</span>
          </div>
          <div class="visit-info-item">
            <span class="visit-info-label">Visits Last Month:</span>
            <span class="visit-info-value">{{ selectedExistingCustomer.visits_last_month || '0' }}</span>
          </div>
          <div class="visit-info-item">
            <span class="visit-info-label">Last Visit Date:</span>
            <span class="visit-info-value">{{ formatLastVisitDate(selectedExistingCustomer.last_visit_date) }}</span>
          </div>
        </div>
      </div>

      <!-- Location (admin) — custom dropdown aligned with Figma / text inputs -->
      <div class="form-section" *ngIf="!isStaff">
        <h4 class="section-subtitle">Location Details</h4>
        <div class="location-field" #locationDropdownRoot (focusin)="onLocationFieldFocusIn()">
          <span class="location-label" id="order-booking-location-label">
            Location <span class="required-star">*</span>
          </span>

          <ng-container *ngIf="isLoadingLocations">
            <div class="location-loading" aria-live="polite">Loading locations…</div>
          </ng-container>

          <ng-container *ngIf="!isLoadingLocations">
            <div class="location-dropdown" [class.is-open]="isLocationMenuOpen">
              <button
                type="button"
                class="location-dropdown-trigger"
                [class.has-value]="!!locationTriggerLabel"
                [class.is-disabled]="locationOptions.length === 0"
                [attr.aria-expanded]="isLocationMenuOpen"
                aria-haspopup="listbox"
                [attr.aria-labelledby]="'order-booking-location-label'"
                [disabled]="locationOptions.length === 0"
                (click)="toggleLocationMenu($event)">
                <span class="location-dropdown-value">{{ locationTriggerLabel || 'Select location' }}</span>
                <svg
                  class="location-dropdown-chevron"
                  [class.rotated]="isLocationMenuOpen"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true">
                  <path
                    d="M3.5 5.25L7 8.75L10.5 5.25"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>

              <div
                *ngIf="isLocationMenuOpen && locationOptions.length > 0"
                class="location-dropdown-panel location-dropdown-panel--fixed"
                role="listbox"
                [ngStyle]="locationPanelFixedStyle"
                [attr.aria-labelledby]="'order-booking-location-label'">
                <button
                  type="button"
                  *ngFor="let loc of locationOptions; trackBy: trackLocationById"
                  role="option"
                  class="location-dropdown-option"
                  [class.is-selected]="loc.id === selectedLocationId"
                  [attr.aria-selected]="loc.id === selectedLocationId"
                  (click)="selectLocationOption(loc, $event)">
                  {{ loc.name }}
                </button>
              </div>
            </div>

            <p *ngIf="locationOptions.length === 0" class="location-empty-hint">
              No locations found for your account. Add a location in settings or refresh the page.
            </p>
          </ng-container>
        </div>
      </div>

    </div>

    <!-- Actions -->
    <div class="customer-info-actions">
      <button class="cancel hide-cancel-mobile" (click)="cancel.emit()">Cancel</button>
      <button class="back mobile-back" (click)="back.emit()">Back</button>
      <div class="footer-right">
        <button class="back desktop-back" (click)="back.emit()">Back</button>
        <button class="continue"
                [disabled]="!isFormValid || isCheckingCustomer || isCreatingCustomer"
                (click)="onContinue()">
          <span *ngIf="isCheckingCustomer">Checking...</span>
          <span *ngIf="isCreatingCustomer">Creating...</span>
          <span *ngIf="!isCheckingCustomer && !isCreatingCustomer">Continue</span>
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Shared customer search dropdown template -->
<ng-template #searchDropdown>
  <div class="search-results-container">
    <div *ngIf="isSearchLoading" class="search-loading">
      <div class="loading-spinner"></div>
      <span>Searching...</span>
    </div>
    <div *ngIf="!isSearchLoading && searchError" class="search-error">
      {{ searchError }}
    </div>
    <div *ngIf="!isSearchLoading && !searchError && searchResults.length === 0 && searchTriggered"
         class="search-no-results">
      No customers found
    </div>
    <div *ngFor="let customer of searchResults; trackBy: trackByIndex"
         class="customer-search-item"
         (click)="onSelectExistingCustomer(customer)">
      <div class="customer-name">
        {{ customer.first_name || customer.firstName || '' }}
        {{ customer.last_name || customer.lastName || '' }}
      </div>
      <div class="customer-phone" *ngIf="customer.phone || customer.contact">
        {{ customer.phone || customer.contact }}
      </div>
      <div class="customer-email-line" *ngIf="customer.email">
        {{ customer.email }}
      </div>
    </div>
  </div>
</ng-template>
